"use client";

import { c as _c } from "react/compiler-runtime";
import { MobileVotingModal, VotingModal } from "@/components/voting";
import VotingModalButton from "@/components/voting/VotingModalButton";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { areSameProfileIdentity } from "@/helpers/ProfileHelpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { useCallback, useState } from "react";
import { useDropInteractionRules } from "@/hooks/drops/useDropInteractionRules";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import WaveDropActions from "../WaveDropActions";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import DropMinimalIdentityRow from "../DropMinimalIdentityRow";
import ParticipationDropContainer from "./ParticipationDropContainer";
import ParticipationDropHeader from "./ParticipationDropHeader";
import ParticipationDropContent from "./ParticipationDropContent";
import ParticipationDropMetadata from "./ParticipationDropMetadata";
import ParticipationDropFooter from "./ParticipationDropFooter";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import ParticipationIdentityProfileCard from "./ParticipationIdentityProfileCard";
import { getParticipationIdentityProfile, getParticipationVisibleMetadata } from "./participationIdentityProfile.helpers";
import type { DropIdentityMode, DropInteractionParams, DropLocation } from "../drop.types";
interface OngoingParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly showInteractions?: boolean | undefined;
}
export default function OngoingParticipationDrop(t0) {
  const $ = _c(85);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuoteClick,
    onDropContentClick,
    identityMode: t1,
    showInteractions: t2
  } = t0;
  const identityMode = t1 === undefined ? "default" : t1;
  const showInteractions = t2 === undefined ? true : t2;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const {
    canShowVote
  } = useDropInteractionRules(drop);
  const isMobile = useIsMobileDevice();
  const isMobileScreen = useIsMobileScreen();
  const hasTouch = useIsTouchDevice() || isMobile;
  let identityProfile;
  let t3;
  let visibleMetadata;
  if ($[0] !== drop.author || $[1] !== drop.metadata || $[2] !== drop.wave) {
    identityProfile = getParticipationIdentityProfile({
      wave: drop.wave,
      metadata: drop.metadata
    });
    let t4;
    if ($[6] !== drop.metadata || $[7] !== drop.wave) {
      t4 = getParticipationVisibleMetadata({
        wave: drop.wave,
        metadata: drop.metadata
      });
      $[6] = drop.metadata;
      $[7] = drop.wave;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    visibleMetadata = t4;
    t3 = identityProfile ? areSameProfileIdentity({
      left: drop.author,
      right: identityProfile
    }) : false;
    $[0] = drop.author;
    $[1] = drop.metadata;
    $[2] = drop.wave;
    $[3] = identityProfile;
    $[4] = t3;
    $[5] = visibleMetadata;
  } else {
    identityProfile = $[3];
    t3 = $[4];
    visibleMetadata = $[5];
  }
  const isSelfNominee = t3;
  const showIdentity = identityMode !== "hidden";
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [isVotingModalOpen, setIsVotingModalOpen] = useState(false);
  let t4;
  if ($[9] !== hasTouch || $[10] !== showInteractions) {
    t4 = () => {
      if (!showInteractions || !hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[9] = hasTouch;
    $[10] = showInteractions;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleLongPress = t4;
  let t5;
  if ($[12] !== activePartIndex || $[13] !== drop || $[14] !== onReply) {
    t5 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[12] = activePartIndex;
    $[13] = drop;
    $[14] = onReply;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const handleOnReply = t5;
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsSlideUp(false);
    };
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleOnAddReaction = t6;
  let t7;
  if ($[17] !== canShowVote || $[18] !== drop || $[19] !== showInteractions) {
    t7 = canShowVote && showInteractions ? <VotingModalButton drop={drop} onClick={() => setIsVotingModalOpen(true)} /> : null;
    $[17] = canShowVote;
    $[18] = drop;
    $[19] = showInteractions;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const voteAction = t7;
  let t8;
  if ($[21] !== activePartIndex || $[22] !== drop || $[23] !== handleOnReply || $[24] !== isMobile || $[25] !== showInteractions || $[26] !== showReplyAndQuote) {
    t8 = !isMobile && showInteractions && showReplyAndQuote && <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} />;
    $[21] = activePartIndex;
    $[22] = drop;
    $[23] = handleOnReply;
    $[24] = isMobile;
    $[25] = showInteractions;
    $[26] = showReplyAndQuote;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== drop || $[29] !== showIdentity) {
    t9 = showIdentity && <WaveDropAuthorPfp drop={drop} />;
    $[28] = drop;
    $[29] = showIdentity;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== drop || $[32] !== identityMode || $[33] !== showIdentity || $[34] !== showWaveInfo) {
    t10 = showIdentity && (identityMode === "minimal" ? <DropMinimalIdentityRow drop={drop} /> : <ParticipationDropHeader drop={drop} showWaveInfo={showWaveInfo} />);
    $[31] = drop;
    $[32] = identityMode;
    $[33] = showIdentity;
    $[34] = showWaveInfo;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== activePartIndex || $[37] !== drop || $[38] !== handleLongPress || $[39] !== onDropContentClick || $[40] !== onQuoteClick) {
    t11 = <ParticipationDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} />;
    $[36] = activePartIndex;
    $[37] = drop;
    $[38] = handleLongPress;
    $[39] = onDropContentClick;
    $[40] = onQuoteClick;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== t10 || $[43] !== t11) {
    t12 = <div className="tw-flex tw-w-full tw-flex-col">{t10}{t11}</div>;
    $[42] = t10;
    $[43] = t11;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== t12 || $[46] !== t9) {
    t13 = <div className="tw-relative tw-z-10 tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-px-4 tw-pt-4 tw-text-left">{t9}{t12}</div>;
    $[45] = t12;
    $[46] = t9;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== drop.id || $[49] !== identityProfile || $[50] !== isSelfNominee || $[51] !== showIdentity) {
    t14 = identityProfile && <div className={`${showIdentity ? "tw-ml-[3.25rem]" : ""} tw-px-4`}><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant="chat" showIdentityHeader={!isSelfNominee} /></div>;
    $[48] = drop.id;
    $[49] = identityProfile;
    $[50] = isSelfNominee;
    $[51] = showIdentity;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== drop.id || $[54] !== visibleMetadata) {
    t15 = <ParticipationDropMetadata metadata={visibleMetadata} contextId={drop.id} />;
    $[53] = drop.id;
    $[54] = visibleMetadata;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  let t16;
  if ($[56] !== drop || $[57] !== showInteractions || $[58] !== voteAction) {
    t16 = <ParticipationDropFooter drop={drop} voteAction={voteAction} showInteractions={showInteractions} />;
    $[56] = drop;
    $[57] = showInteractions;
    $[58] = voteAction;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  let t17;
  if ($[60] !== t14 || $[61] !== t15 || $[62] !== t16) {
    t17 = <div className="tw-flex tw-w-full tw-flex-col">{t14}{t15}{t16}</div>;
    $[60] = t14;
    $[61] = t15;
    $[62] = t16;
    $[63] = t17;
  } else {
    t17 = $[63];
  }
  let t18;
  if ($[64] !== drop || $[65] !== isMobileScreen || $[66] !== isVotingModalOpen || $[67] !== showInteractions) {
    t18 = showInteractions && (isMobileScreen ? <MobileVotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} /> : <VotingModal drop={drop} isOpen={isVotingModalOpen} onClose={() => setIsVotingModalOpen(false)} />);
    $[64] = drop;
    $[65] = isMobileScreen;
    $[66] = isVotingModalOpen;
    $[67] = showInteractions;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  let t19;
  if ($[69] !== drop || $[70] !== handleOnReply || $[71] !== isSlideUp || $[72] !== longPressTriggered || $[73] !== showInteractions || $[74] !== showReplyAndQuote) {
    t19 = showInteractions && <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[69] = drop;
    $[70] = handleOnReply;
    $[71] = isSlideUp;
    $[72] = longPressTriggered;
    $[73] = showInteractions;
    $[74] = showReplyAndQuote;
    $[75] = t19;
  } else {
    t19 = $[75];
  }
  let t20;
  if ($[76] !== drop || $[77] !== isActiveDrop || $[78] !== location || $[79] !== t13 || $[80] !== t17 || $[81] !== t18 || $[82] !== t19 || $[83] !== t8) {
    t20 = <ParticipationDropContainer drop={drop} isActiveDrop={isActiveDrop} location={location}>{t8}{t13}{t17}{t18}{t19}</ParticipationDropContainer>;
    $[76] = drop;
    $[77] = isActiveDrop;
    $[78] = location;
    $[79] = t13;
    $[80] = t17;
    $[81] = t18;
    $[82] = t19;
    $[83] = t8;
    $[84] = t20;
  } else {
    t20 = $[84];
  }
  return t20;
}